'use client';
import React, {useEffect, useRef} from "react"
import Router from "next/router";

import Home from "./Home/index"
import PaiementPage from "./Paiement";
import Error404 from "../../components/404";
import ApiService from "../../services/ApiService";
import {uuidv4} from "../../utils";
import {useIdleTimer} from "react-idle-timer";
import {applyLang, useTranslator} from "../../i18n";
import HomeAld from "./Home/ald";
import HomeV2 from "./V2";
import {isCampagneV2} from "./V2/detect";
import {isPreviewRequest} from "./V2/preview";
import CrashReloader from "../../components/CrashReloader";

//SCREENS
import ScreenAmbs from "./Screen/Ambs";
import ScreenGala from "./Screen/Gala";
import CustomBMRDT from "./Screen/CustomBMRDT";
import CustomSMaur from "./Screen/CustomSMaur";
import YoudTetKislev1 from "./Screen/YoudTetKislev1";
import YoudTetKislev2 from "./Screen/YoudTetKislev2";
import CustomChneor2 from "./Screen/CustomChneor2";
import CustomCantonVert from "./Screen/CustomCantonVert";
import Custom17Terne from "./Screen/Custom17Terne";
import CustomBlEtudiantV3 from "./Screen/CustomBlEtudiantV3";
import CustomRouan from "./Screen/CustomRouan";
import CustomBhParis12 from "./Screen/CustomBhParis12";
import CustomSaclay from "./Screen/CustomSaclay";
import CustomMarseille9sud from "./Screen/CustomMarseille9sud";
import CustomPerp from "./Screen/CustomPerp";
import CustomBrunoy from "./Screen/CustomBrunoy";
import CustomBrunoy2 from "./Screen/CustomBrunoy2";
import CustomChatou from "./Screen/CustomChatou";
import CustomYTB from "./Screen/CustomYTB";
import CustomTalpiot from "./Screen/CustomTalpiot";
import CustomNahariya61 from "./Screen/CustomNahariya61";
import logger from "../../services/logger"
import WhatsappFab from "../../components/WhatsappFab";
import { loadPaiementConfig } from "../../services/paiementLauncher";

function getInitialTeam(campagneData,team)
{
    // Une équipe masquée (ambassadeur refusé ou en attente) n'a pas de page de don :
    // son lien retombe sur la campagne, sans lui attribuer le moindre don.
    if(campagneData && team && Array.isArray(campagneData.teams) && campagneData.teams.find(i=>i.param === team && !i.hidden)){
        return team
    }

    return null
}

// Résolution multilingue d'une campagne. applyLang(campagneData) ne couvre que
// les clés racine (campagneData[lang]). Or les textes éditables (donorName,
// donationButtonText…) sont stockés DANS le config (config[lang][clé]) → il faut
// appliquer applyLang AUSSI sur config, sinon la traduction n'est jamais prise.
function localizeCampagne(campagneData, lang)
{
    if(!campagneData) return campagneData
    // applyLang/_.merge mute son 1er argument → on lui passe des COPIES pour ne
    // jamais muter le state React (campagneData / son config d'origine).
    const localized = applyLang({...campagneData}, lang)
    if(localized?.config){
        localized.config = applyLang({...localized.config}, lang)
    }
    return localized
}

function getInitialSource(campagneData,source)
{
    if(campagneData && source && Array.isArray(campagneData.sources) && campagneData.sources.find(i=>i.param === source)){
        return source
    }

    return null
}

export default function ThemeCampagne({context})
{
    const [isMobile, setIsMobile] = React.useState(context.isMobile);

    const initialTeam=getInitialTeam(context?.data && context?.data[0],context.route[0])
    const initialSource=getInitialSource(context?.data && context?.data[0],context.query.s)

    const [campagneTarget, setCampagneTarget] = React.useState({team:initialTeam,source:initialSource,initial:{team:initialTeam,source:initialSource}});
    const [campagneData, setCampagneData] = React.useState(context?.data?context?.data[0]:null);

    const [loading, setLoading] = React.useState(false);
    const isIdle=useRef(false)
    const intervalCamp = React.useRef(null);
    const campagneTargetRef = React.useRef(campagneTarget);

    const isScreen=Boolean(context.route.length===1 && context.route[0]==="__screen" && campagneData?.screenId)
    logger.debug({ isScreen }, "isScreen")

    const isScreen2=Boolean(context.route.length===1 && context.route[0]==="__screen2" && campagneData?.screenId)
    logger.debug({ isScreen2 }, "isScreen2")

    useIdleTimer({
        onIdle:()=>{
            logger.debug("onIdle")
            isIdle.current=true
        },
        onActive:()=>{
            logger.debug("onActive")
            isIdle.current=false
        },
        timeout: 60*1000,
    })

    const initLoader=()=>
    {
        const mobileObserverUnsubscribe=initMobileObserver()

        const start = () => {
            logger.debug("start");
            setLoading(true);
        };
        const end = () => {
            logger.debug("findished");
            setLoading(false);
        };
        Router.events.on("routeChangeStart", start);
        Router.events.on("routeChangeComplete", end);
        Router.events.on("routeChangeError", end);
        return () => {
            mobileObserverUnsubscribe();
            Router.events.off("routeChangeStart", start);
            Router.events.off("routeChangeComplete", end);
            Router.events.off("routeChangeError", end);
        };
    }

    const initMobileObserver=()=>{
        const observer=new ResizeObserver(function() {
            const _width=document.body.offsetWidth
            const _isMobile=_width<=768
            //console.log("mobileObserver",_width,_isMobile)
            setIsMobile(_isMobile)
        });
        observer.observe(document.body);
        return () => observer.disconnect();
    }

    const getStorage=()=>{
        try {
            return window.localStorage;
        } catch (e) {
            return null;
        }
    }

    const getVisitor=()=>{
        let _visitor=getStorage()?.getItem('VISITOR')
        if(!_visitor){
            _visitor=uuidv4()
            getStorage()?.setItem('VISITOR',_visitor)
        }
        return _visitor
    }

    const getCampagne=async (campagneId)=>{
        logger.debug({ campagneId }, "getCampagne")
        const res=await ApiService.get("/campagne/id/"+campagneId)
        if(res?.data)
        {
            setCampagneData({...res.data,_liveRevision:Date.now()})
        }
    }

    const pingCampagne=async (campagneId)=>{
        logger.debug({ isIdle: isIdle.current }, "isIdle")
        if (document.hidden || isIdle.current) {
            return false
        }

        logger.debug({ campagneId }, "pingCampagne")
        const _visitor=getVisitor()

        const currentTarget=campagneTargetRef.current
        const res=await ApiService.post("/campagne/ping/"+campagneId+"/"+_visitor, {
            target:{source:currentTarget.source,team:currentTarget.team,url:window.location.href}
        })
        if(res?.data)
        {
            setCampagneData({...res.data,_liveRevision:Date.now()})
            setCampagneTarget((previous)=>{
                const next={...previous,...res.target}
                campagneTargetRef.current=next
                return next
            })
        }

    }

    const initCampagne=(campagneId)=>
    {
        if(!campagneId){ return ()=>{} }
        // Aperçu depuis le back-office : ni comptage de visiteur ni rafraîchissement.
        // Le responsable qui relit sa page ne doit pas apparaître dans ses statistiques.
        if(isPreviewRequest(context)){ return ()=>{} }

        let stopped=false
        let running=false
        const screenMode=isScreen || isScreen2
        const task=screenMode ? getCampagne : pingCampagne

        const run=async()=>{
            if(stopped || running){ return }
            running=true
            clearTimeout(intervalCamp.current)
            try{
                await task(campagneId)
            }catch(error){
                logger.warn({error:error?.message,campagneId},"campaign live refresh failed")
            }finally{
                running=false
                if(!stopped){ intervalCamp.current=setTimeout(run,5000) }
            }
        }

        const onVisibilityChange=()=>{
            if(!screenMode && !document.hidden){ run() }
        }

        run()
        if(!screenMode){ window.addEventListener("visibilitychange",onVisibilityChange) }

        return ()=>{
            stopped=true
            window.removeEventListener("visibilitychange",onVisibilityChange)
            clearTimeout(intervalCamp.current)
        }
    }



    React.useEffect(() => {
        let campagneId=campagneData?.id
        if(!campagneId){
            // Passe par le lanceur : après une sortie d'iframe, la config du don
            // voyage dans l'URL et non plus dans le stockage de session (cloisonné
            // par site hôte). Lire le stockage en direct laissait la page de
            // campagne sans identifiant, donc vide, alors que la config était là.
            const config=loadPaiementConfig(context.org)
            campagneId=config?.typeId
        }

        const loaderUnsubscribe=initLoader()
        const campagneUnsubscribe=initCampagne(campagneId)

        return ()=>{
            loaderUnsubscribe()
            campagneUnsubscribe()
        }
    }, []);



    const getDonorName=(nb)=>{
        const _campagneData=localizeCampagne(campagneData, context.lang)

        const _donorName=_campagneData.config?.donorName || "Parrainage"
        if(nb<=1){
            return _donorName
        }

        return _campagneData.config?.donorNameMulti ||  _donorName+"s"
    }


    var page=context.page
    //console.log("PAGE",page)
    logger.debug({ isMobile }, "isMobile")

    const T=useTranslator(context)

    const _context={...context,isMobile:isMobile,getDonorName:getDonorName,T:T}

    const _campagneData=localizeCampagne(campagneData, context.lang)

    //RENDER

    if(!campagneData){
        return null
    }

    // Factorisation : on choisit le composant à rendre, puis on wrap
    // une seule fois dans CrashReloader (swallow "t is not a function").
    const renderContent = () => {
        if(isScreen) {
            switch (campagneData.screenId) {
                case "classic-ambs":
                    return <ScreenAmbs context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "classic-gala":
                    return <ScreenGala context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "custom-bmrdt":
                    return <CustomBMRDT context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "custom-smaur":
                    return <CustomSMaur context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "YoudTetKislev1":
                    return <YoudTetKislev1 context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "custom-chneor":
                    return <CustomChneor2 context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "custom-canton-vert":
                    return <CustomCantonVert context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "custom-17terne":
                    return <Custom17Terne context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "CustomBlEtudiantV3":
                    return <CustomBlEtudiantV3 context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "custom-rouan":
                    return <CustomRouan context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                case "custom-bh-paris12":
                    return <CustomBhParis12 context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>

                case "custom-saclay":
                    return <CustomSaclay context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>

                case "custom-brunoy":
                    return <CustomBrunoy context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>

                case "custom-chatou":
                    return <CustomChatou context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>


                case "custom-ytb":
                    return <CustomYTB context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>

                case "custom-talpiot":
                    return <CustomTalpiot context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>

                case "custom-nahariya-61":
                    return <CustomNahariya61 context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>


                default:
                    return <Error404/>
            }
        }

        if(isScreen2){
            return <YoudTetKislev2 context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
        }

        switch (page.type) {
            case "CAMPAGNE":
                // Nouvelle page (réglage « V2 », ou aperçu ?design=v2 ; ?design=v1 force l'ancienne).
                if(isCampagneV2(campagneData, context.query)){
                    return <HomeV2 context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                }
                switch(campagneData?.config.template) {
                    case "ALD":
                        return <HomeAld context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                    default:
                        return <Home context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>
                }

            case "PAIEMENT":
                return <PaiementPage context={_context} loading={loading} campagneData={_campagneData} campagneTarget={campagneTarget}/>

            default:
                return <Error404/>
        }
    };

    return (
        <CrashReloader pageKey={page?.id || page?.type}>
            {renderContent()}
            <WhatsappFab context={_context} />
        </CrashReloader>
    );

}
